import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Music, Pause, Play, X } from "lucide-react";

import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { LAYOUT } from "../../../../shared/layout";
import { formatDuration } from "../../domain/types";

type Props = {
    /** What is playing: a track's name, a meditation's title. */
    title: string;
    /** Whose it is, when that is known. */
    author?: string | null;
    paused: boolean;
    /** Seconds in, and how long it runs. */
    position: number;
    duration: number;
    onToggle: () => void;
    onSeek: (seconds: number) => void;
    onClose: () => void;
};

/**
 * What is playing, along the bottom of the screen while it plays: a track
 * from a shelf, or a meditation's recording.
 *
 * A small icon changing in one row of a long shelf was easy to lose: scroll
 * away and music plays with nothing on screen saying what or where to stop it.
 * The bar stays in view with the name, where it has got to, and the controls.
 * On a phone it sits above the bottom navigation rather than under it.
 */
export default function AudioPlayerBar({
    title,
    author,
    paused,
    position,
    duration,
    onToggle,
    onSeek,
    onClose,
}: Props) {
    const { t } = useTranslation();
    const { isSmallScreen } = useBreakpoint();
    // Without a length there is nothing to show progress against: the bar
    // waits rather than rescaling to "as far as it has played" on every tick.
    const known = duration > 0;
    const length = known ? Math.max(duration, position) : 0;

    // Room under the page while the bar is up, so the end of what is behind
    // it — a last track, a Publish button — can still be scrolled into view.
    useEffect(() => {
        document.body.classList.add("pb-32");
        return () => document.body.classList.remove("pb-32");
    }, []);

    return (
        <section
            aria-label={t("meditation.player.label")}
            className="fixed left-1/2 z-40 w-[min(40rem,calc(100%-2rem))] -translate-x-1/2 rounded-2xl border border-border-subtle bg-surface-raised px-4 py-3 shadow-theme-lg"
            style={{
                bottom: isSmallScreen
                    ? `calc(${LAYOUT.NAV_HEIGHT.BOTTOM_BAR} + ${LAYOUT.SAFE_AREA_PADDING} + 0.75rem)`
                    : "1.5rem",
            }}
        >
            <div className="flex items-center gap-3">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent-tint text-accent-on-tint">
                    <Music size={18} aria-hidden="true" className={paused ? undefined : "animate-pulse"} />
                </span>

                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-content-primary">{title}</p>
                    <p className="truncate text-xs text-content-tertiary">
                        {author
                            ? t("meditation.byAuthor", { author })
                            : t(paused ? "meditation.player.paused" : "meditation.player.playing")}
                    </p>
                </div>

                <button
                    type="button"
                    onClick={onToggle}
                    aria-label={t(paused ? "meditation.player.resume" : "meditation.player.pause", {
                        track: title,
                    })}
                    className="flex size-11 shrink-0 items-center justify-center rounded-full bg-accent text-accent-contrast hover:bg-accent-hover"
                >
                    {paused ? <Play size={18} aria-hidden="true" /> : <Pause size={18} aria-hidden="true" />}
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label={t("meditation.player.close")}
                    className="flex size-11 shrink-0 items-center justify-center rounded-lg text-content-secondary hover:bg-surface-sunken"
                >
                    <X size={18} aria-hidden="true" />
                </button>
            </div>

            <div className="mt-2 flex items-center gap-3 text-xs tabular-nums text-content-tertiary">
                <span className="w-10 text-right">{formatDuration(Math.floor(position))}</span>
                <input
                    type="range"
                    min={0}
                    max={length || 0}
                    step="any"
                    value={Math.min(position, length)}
                    disabled={!known}
                    onChange={(e) => onSeek(Number(e.target.value))}
                    aria-label={t("meditation.player.seek")}
                    aria-valuetext={
                        known
                            ? `${formatDuration(Math.floor(position))} / ${formatDuration(Math.floor(length))}`
                            : formatDuration(Math.floor(position))
                    }
                    className="h-1.5 flex-1 cursor-pointer accent-[var(--color-accent)]"
                />
                <span className="w-10">{known ? formatDuration(Math.floor(length)) : "–:––"}</span>
            </div>
        </section>
    );
}
